// NSARA Stores V2 — "Mes boutiques" (section 4 de la mission).
//
// Remplace l'ancien écran CRUD au formulaire de création permanent: la liste
// est maintenant une grille de cartes boutique (1 par ligne à 390px, 2 à 620px,
// 3 à 980px) et la création passe par une modale à 2 champs
// (voir boutiques/CreateShopDialog.js).
//
// Source de vérité: /api/shops via le client API centralisé — jamais
// localStorage ni le shim Firestore. Les compteurs affichés (produits,
// commandes, ventes) viennent de `includeStats=true` (backend/routes/shops.js):
// ce sont des données RÉELLES. Aucune métrique n'est inventée — si le backend
// ne renvoie pas de stats, la carte n'affiche simplement aucun chiffre.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, ExternalLink, Plus, AlertCircle } from 'lucide-react';
import { getShops } from '../services/apiClient';
import { useBusinessContext } from '../hooks/useBusinessContext';
import CreateShopDialog from './boutiques/CreateShopDialog';
import { formatMoneyShort, shopMonogram, buildPublicStoreUrl } from './boutiques/storeUtils';
import '../styles/stores.css';

export default function BoutiqueModule() {
  const navigate = useNavigate();
  const { businessId, loading: contextLoading, error: contextError, reload } = useBusinessContext();
  const [shops, setShops] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getShops(businessId, { includeStats: true });
      setShops(res.shops || []);
      setStats(res.stats || {});
    } catch (err) {
      setError(err?.message || 'Impossible de charger vos boutiques.');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = async (shop) => {
    setCreateOpen(false);
    // Le Provider partagé doit connaître la nouvelle boutique (sélecteur de
    // boutique, module Ventes, Inventaire…) avant d'ouvrir son workspace.
    await reload();
    // Le bandeau de succès est porté par le workspace (state de navigation):
    // naviguer immédiatement aurait démonté cet écran avant tout affichage.
    navigate(`/boutique/${shop.id}`, { state: { createdShopName: shop.name } });
  };

  const busy = loading || (contextLoading && shops.length === 0);

  return (
    <div className="stores-page">
      <header className="stores-head">
        <div className="stores-head-text">
          <h1 className="stores-title">Mes boutiques</h1>
          <p className="stores-subtitle">Gérez tous vos commerces depuis un seul espace.</p>
        </div>
        <button type="button" className="stores-btn stores-btn--primary" onClick={() => setCreateOpen(true)}>
          <Plus size={17} aria-hidden="true" /> Créer une boutique
        </button>
      </header>

      {(error || contextError) && (
        <p className="stores-alert stores-alert--error" role="alert">
          <AlertCircle size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
          {error || contextError}
        </p>
      )}

      {busy && (
        <div className="stores-grid" aria-busy="true" aria-label="Chargement de vos boutiques">
          {[0, 1, 2].map((key) => <div key={key} className="stores-skeleton-row" />)}
        </div>
      )}

      {!busy && shops.length === 0 && (
        <div className="stores-empty">
          <span className="stores-empty-icon" aria-hidden="true"><Store size={22} /></span>
          <h3>Créez votre première boutique</h3>
          <p>
            Une boutique réunit vos produits, vos commandes et sa vitrine publique.
            Deux minutes suffisent pour la mettre en ligne.
          </p>
          <button type="button" className="stores-btn stores-btn--primary" onClick={() => setCreateOpen(true)}>
            <Plus size={17} aria-hidden="true" /> Créer une boutique
          </button>
        </div>
      )}

      {!busy && shops.length > 0 && (
        <div className="stores-grid">
          {shops.map((shop) => {
            const shopStats = stats[shop.id];
            const paused = shop.active === false;
            return (
              <article className="stores-shop-card" key={shop.id}>
                <div className="stores-shop-top">
                  <span className="stores-shop-mark" aria-hidden="true">
                    {shop.logo_url ? <img src={shop.logo_url} alt="" /> : shopMonogram(shop.name)}
                  </span>
                  <div className="stores-shop-id">
                    <h2 className="stores-shop-name">{shop.name}</h2>
                    {shop.description && <p className="stores-shop-desc">{shop.description}</p>}
                    <span className={paused ? 'stores-badge stores-badge--paused stores-shop-status' : 'stores-badge stores-badge--active stores-shop-status'}>
                      {paused ? 'En pause' : 'Active'}
                    </span>
                  </div>
                </div>

                {shopStats && (
                  <div className="stores-shop-stats">
                    <p className="stores-shop-stat"><strong>{shopStats.productsCount}</strong> produits</p>
                    <p className="stores-shop-stat"><strong>{shopStats.ordersCount}</strong> commandes</p>
                    {Number(shopStats.revenue) > 0 && (
                      <p className="stores-shop-stat"><strong>{formatMoneyShort(shopStats.revenue)}</strong> de ventes</p>
                    )}
                  </div>
                )}

                <div className="stores-shop-actions">
                  <button type="button" className="stores-btn stores-btn--primary stores-btn--sm" onClick={() => navigate(`/boutique/${shop.id}`)}>
                    Gérer la boutique
                  </button>
                  <a
                    className="stores-shop-link"
                    href={buildPublicStoreUrl(shop.id)}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={`Voir la vitrine publique de ${shop.name} (nouvel onglet)`}
                  >
                    Voir la boutique <ExternalLink size={14} aria-hidden="true" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <CreateShopDialog
        open={createOpen}
        businessId={businessId}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
